import React, { createContext, useContext, useState, useEffect } from 'react'
import { trackConciergeViewedorDismissed } from '../events'

type View = 'explainer' | 'content'

interface FabContext {
  isFabVisible: boolean
  view: View
  changeView: (view: View) => void
  isConciergeOpened: boolean
  toggleConcierge: ({ track }: { track: boolean }) => void
}

export const FabContext = createContext<FabContext>({
  isFabVisible: false,
  view: 'content',
  changeView: () => {},
  isConciergeOpened: false,
  toggleConcierge: () => {},
})

export const FabProvider = ({ children }: { children: React.ReactNode }) => {
  const [isConciergeOpened, setIsConciergeOpened] = useState(false)
  const [isFabVisible, setIsFabVisible] = useState(false)
  const [view, setView] = useState<View>('content')

  useEffect(() => {
    const bottomBuffer = 580 // From DS sticky footer

    const handleScroll = () => {
      const topOfPage = window.scrollY <= 65
      const footerInView =
        window.scrollY + window.innerHeight >
        document.body.offsetHeight - bottomBuffer

      const shouldShowFab = !topOfPage && !footerInView
      if (shouldShowFab !== isFabVisible) {
        setIsFabVisible(shouldShowFab)
        if (isConciergeOpened) {
          trackConciergeViewedorDismissed({
            show: shouldShowFab,
            manuallyDismissed: false,
          })
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isConciergeOpened, isFabVisible])

  const toggleConcierge = (
    { track }: { track?: boolean } = { track: true },
  ) => {
    setIsConciergeOpened((prev) => {
      if (track) {
        trackConciergeViewedorDismissed({
          show: !prev,
          manuallyDismissed: true,
        })
      }
      return !prev
    })
  }

  return (
    <FabContext.Provider
      value={{
        isFabVisible,
        changeView: setView,
        view,
        toggleConcierge,
        isConciergeOpened,
      }}
    >
      {children}
    </FabContext.Provider>
  )
}

export const useFab = () => {
  const context = useContext(FabContext)

  if (!context) {
    throw new Error('useFab must be used within a FabProvider')
  }

  return context
}
